<template>
  <div class="auth-shell">
    <div class="auth-shell__mobile-logo lg:hidden">
      <img
        src="@/assets/images/logo-tc-black.svg"
        alt="ShopiMind"
      >
    </div>

    <div
      v-if="isLoadingForm"
      class="auth-shell__loader"
    >
      <i class="pi pi-spin pi-spinner" />
    </div>

    <header class="auth-shell__header">
      <h1 class="auth-shell__title">
        {{ t('user.register.welcome') }}
      </h1>
      <p class="auth-shell__intro">
        {{ t('user.register.title') }}
      </p>
    </header>

    <div class="auth-shell__body">
      <!-- Section: Account -->
      <section class="auth-shell__section">
        <header class="auth-shell__section-header">
          <span class="auth-shell__section-icon">
            <i class="far fa-user" />
          </span>
          <h3 class="auth-shell__section-title">
            Votre compte
          </h3>
        </header>

        <div class="auth-shell__field auth-shell__field--full">
          <BaseTextInput
            v-model="userInfo.email"
            field-id="email"
            label="user.register.email"
            input-style="width: 100%;"
            :errors="formValidation"
          />
        </div>

        <div class="auth-shell__grid">
          <div class="auth-shell__field">
            <BaseTextInput
              v-model="userInfo.password"
              type="password"
              input-type="password"
              field-id="password"
              label="user.register.password"
              input-style="width: 100%;"
              :errors="formValidation"
            />
          </div>
          <div class="auth-shell__field">
            <BaseTextInput
              v-model="userInfo.confirmPassword"
              type="password"
              input-type="password"
              field-id="confirmPassword"
              label="user.register.confirmPassword"
              input-style="width: 100%;"
              :errors="formValidation"
            />
          </div>
        </div>
      </section>

      <!-- Section: Shop -->
      <section class="auth-shell__section">
        <header class="auth-shell__section-header">
          <span class="auth-shell__section-icon">
            <i class="far fa-store" />
          </span>
          <h3 class="auth-shell__section-title">
            Votre boutique
          </h3>
        </header>

        <div class="auth-shell__field auth-shell__field--full">
          <BaseTextInput
            v-model="userInfo.shopUrl"
            field-id="shopUrl"
            label="user.register.shopUrl"
            input-style="width: 100%;"
            placeholder="https://..."
            :errors="formValidation"
          />
        </div>

        <div class="auth-shell__grid">
          <div class="auth-shell__field">
            <label class="auth-shell__label">
              {{ t('user.register.ecommerce') }}
            </label>
            <Dropdown
              v-model="userInfo.solutionEcommerce"
              :options="solutions"
              option-label="label"
              option-value="code"
              :placeholder="t('user.register.ecommerce')"
              class="auth-shell__select"
            />
            <FieldErrors
              :key="componentFieldErrorsKey"
              :errors="formValidation"
              field="solutionEcommerce"
            />
          </div>
          <div class="auth-shell__field">
            <BaseTextInput
              v-model="userInfo.promoCode"
              field-id="promoCode"
              label="user.register.promoCode"
              input-style="width: 100%;"
              :errors="formValidation"
            />
          </div>
        </div>
      </section>

      <!-- Terms -->
      <div class="auth-shell__terms">
        <Checkbox
          id="binary"
          v-model="userInfo.acceptTAC"
          binary
        />
        <span>
          {{ t('user.register.acceptText') }}
          <a
            href="#"
            class="auth-shell__link"
            @click.prevent="showTACModal = true"
          >{{ t('user.register.TAC') }}</a>
        </span>
      </div>
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="acceptTAC"
      />

      <!-- Submit -->
      <SpmButton
        :label="t('user.register.submit')"
        :loading="isLoadingForm"
        style="width: 100%"
        class="flex p-button p-button-success auth-shell__btn"
        @click="validateRegisterForm"
      />

      <p class="auth-shell__foot">
        {{ t('user.register.accountPresent') }}
        <router-link
          :to="{ name: 'login' }"
          class="auth-shell__link auth-shell__link--bold"
        >
          {{ t('user.register.logIn') }}
        </router-link>
      </p>
    </div>

    <TermsAndConditions
      v-if="showTACModal"
      @on-close-modal="showTACModal = false"
    />
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import {
  defineComponent, onBeforeMount, onMounted, onUnmounted, reactive, ref,
} from 'vue';
import TermsAndConditions from '@/views/auth/TermsAndConditions.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import {
  helpers, maxLength, minLength, required, sameAs,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import solutions from '@/configs/ecommerce-solutions';
import { emailFormatValidator, urlFormatValidator } from '@/helpers/CustomValidator';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { useRouter } from 'vue-router';
import { getItemFromLocalStorageWithExpiry, showToastError, showToastSuccess } from '@/helpers';
import { getConfigurationKey } from '@/composables/configs/configuration';

export default defineComponent({
  name: 'SpeedRegistration',
  components: {
    FieldErrors,
    TermsAndConditions,
    Dropdown,
    SpmButton,
    Checkbox,
    BaseTextInput,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const isLoadingForm = ref(false);
    const formValidation = ref<any>({});
    const componentFieldErrorsKey = ref(0);
    const showTACModal = ref(false);

    const promoCodePartner = ref(getItemFromLocalStorageWithExpiry('promoCodePartner') ?? '');
    const userInfo = reactive({
      email: '',
      password: '',
      confirmPassword: '',
      promoCode: promoCodePartner.value,
      shopUrl: '',
      solutionEcommerce: '',
      acceptTAC: false,
    });

    const validateRegisterFormData = async (): Promise<ErrorConfigForm> => {
      const rules = {
        email: {
          emailFormatValidator: emailFormatValidator(),
        },
        password: {
          required,
          minLength: minLength(8),
          maxLength: maxLength(40),
        },
        confirmPassword: {
          required,
          sameAsPassword: sameAs(userInfo.password),
        },
        shopUrl: {
          urlFormatValidator: urlFormatValidator(),
        },
        solutionEcommerce: {
          required,
        },
        acceptTAC: {
          isChecked: helpers.withMessage(t('customValidator.acceptTAC'), (value: any) => value),
        },
      };

      const v$ = useVuelidate(rules, userInfo);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const validateRegisterForm = async () => {
      isLoadingForm.value = true;
      const validation = await validateRegisterFormData();
      if (!validation.success) {
        formValidation.value = validation.validate;
        componentFieldErrorsKey.value += 1;
        isLoadingForm.value = false;
      } else {
        try {
          const result = await nestCall('post', NestApiType.AUTH, '/auth/register', {}, '', {
            email: userInfo.email,
            password: userInfo.password,
            shopUrl: userInfo.shopUrl,
            solutionEcommerce: userInfo.solutionEcommerce,
            promoCode: userInfo.promoCode,
          }, false);

          if (result && result.success) {
            await showToastSuccess(t('user.register.actionRegistration.success'));
            await router.push('/auth/login');
          } else {
            const reasonError = result.reason ?? '';
            switch (reasonError) {
              case 'data_incorrect':
                await showToastError(t('user.register.actionRegistration.dataIncorrect'));
                break;
              case 'email_already_exist':
                await showToastError(t('user.register.actionRegistration.emailAlreadyExist'));
                break;
              default:
                await showToastError(t('errorMessages.GENERIC_ERROR'));
            }
          }
        } catch (e) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        }

        isLoadingForm.value = false;
      }
    };

    onBeforeMount(() => {
      document.body.classList.add('no_expand');
    });

    onMounted(async () => {
      document.body.classList.add('no_expand');
    });

    onUnmounted(() => {
      document.body.classList.remove('no_expand');
    });

    return {
      t,
      formValidation,
      componentFieldErrorsKey,
      userInfo,
      isLoadingForm,
      solutions,
      showTACModal,
      validateRegisterForm,
    };
  },
});
</script>

<style scoped lang="scss">
.auth-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 36rem;
  margin: 0 auto;
  padding: 0.5rem 0;

  &__mobile-logo {
    margin-bottom: 1.25rem;
    img { height: 28px; width: auto; }
  }

  &__loader {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    i { font-size: 1.5rem; color: #94c840; }
  }

  &__header {
    margin-bottom: 1.75rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.6rem;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }

  &__intro {
    font-size: 0.92rem;
    color: #475569;
    margin: 0;
    line-height: 1.55;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__section-header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.1rem;
  }

  &__section-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.14);
    color: #5d8a24;
    font-size: 0.82rem;
  }

  &__section-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: 0.01em;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__select {
    width: 100%;

    :deep(&.p-dropdown) {
      height: 2.3rem;
      border-radius: 0.5rem;
      border-color: #d1d5db;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      &:hover { border-color: #94c840; }
      &.p-focus {
        border-color: #94c840;
        box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
      }

      .p-dropdown-label {
        font-size: 0.9rem;
        display: flex;
        align-items: center;
      }
    }
  }

  &__terms {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin: 0.25rem 0 0.5rem;

    span {
      font-size: 0.8125rem;
      color: #475569;
      line-height: 1.5;
    }

    :deep(.p-checkbox .p-checkbox-box) {
      border-radius: 4px;
      margin-top: 2px;
      &.p-highlight {
        background: #94c840;
        border-color: #94c840;
      }
    }
  }

  &__link {
    color: #94c840;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
    &:hover { color: #7eb52e; }

    &--bold { font-weight: 700; }
  }

  &__btn :deep(.p-button) {
    height: 2.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    background: #94c840;
    border-color: #94c840;
    transition: all 0.2s ease;

    &:hover {
      background: #84b738;
      border-color: #84b738;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(148, 200, 64, 0.3);
    }
    &:active { transform: translateY(0); }
  }

  &__foot {
    text-align: center;
    margin: 0.75rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;
  }
}

/* BaseTextInput label + input alignment with user-infos */
:deep(.field-wrapper .field-label) {
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #475569 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem !important;
}

:deep(.p-inputtext),
:deep(.p-password-input) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover { border-color: #94c840; }
  &:focus,
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.p-password) { width: 100%; }

@media (max-width: 640px) {
  .auth-shell__grid {
    grid-template-columns: 1fr;
  }
}
</style>
